import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import {
  IconDownload,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { Text } from '@contrast-security-inc/design-system-typography'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import {
  WizardStepContext,
  WizardStepContextValueType
} from '../agent-wizard-steps'
import { RecommendedOption } from '../step-selector'

import {
  logo_docker,
  logo_kubernetes,
  logo_linux,
  logo_os,
  logo_windows
} from '../images'
import {
  populateCode,
  useDownloadCodeblock
} from '../common/download-codeblock'
import { ImageIcon } from '../common/image-icon'
import { AgentConfig } from '../agent-config'
import { getOrgUuid, getCurrentLanguage } from '../common/utils'
import { downloadAgent } from '../../../download-agent'
import {
  getApplicationDrilldownRoute,
  getApplicationsRoute
} from '../../../../../components/route'
import { formatStyles } from '../../../../../components/react-intl-helpers'
import { OPERATING_SYSTEM } from '../common/constants'
import { CodeBlock } from '@contrast-security-inc/design-system-surfaces'

const useYamlPath = () => {
  const ctx = React.useContext(WizardStepContext)
  const language = getCurrentLanguage().toLocaleLowerCase().replace(/_/g, '-')
  const osType = ctx.branches[OPERATING_SYSTEM.KEY]
  const path =
    osType === OPERATING_SYSTEM.WINDOWS
      ? `%ProgramData%\\Contrast\\${language}\\contrast_security.yaml`
      : `/etc/contrast/${language}/contrast_security.yaml`

  return path
}
// TABLE COLUMNS & DATA
const getColumnsEnv = (intl) => [
  {
    accessorKey: 'var',
    header: intl.formatMessage({ id: 'title_environmentVariable' })
  },
  {
    accessorKey: 'val',
    header: intl.formatMessage({ id: 'placeholder_value' }),
    cell: (info) => (
      <div
        style={{
          wordBreak: 'break-all',
          overflowWrap: 'break-word',
          whiteSpace: 'normal'
        }}
      >
        {info.getValue()}
      </div>
    )
  }
]
const getEnvVarsData = (type) => {
  const vars =
    type === 'windows'
      ? ['win-x64', 'win-x86']
      : type === 'linux'
      ? ['linux-x64']
      : ['linux-arm64']

  const data =
    type === 'windows'
      ? [
          {
            var: 'CORECLR_PROFILER_PATH_64',
            val: `<DirectoryRoot>\\contrast\\runtimes\\${vars[0]}\\native\\ContrastProfiler.dll`
          },
          {
            var: 'CORECLR_PROFILER_PATH_32',
            val: `<DirectoryRoot>\\contrast\\runtimes\\${vars[1]}\\native\\ContrastProfiler.dll`
          },
          {
            var: 'CORECLR_PROFILER',
            val: '{8B2CE134-0948-48CA-A4B2-80DDAD9F5791}'
          },
          { var: 'CORECLR_ENABLE_PROFILING', val: '1' },
          {
            var: 'CONTRAST_CONFIG_PATH',
            val: '<path_to_contrast_security.yaml>'
          }
        ]
      : [
          {
            var: 'CORECLR_PROFILER_PATH_64',
            val: `<DirectoryRoot>/contrast/runtimes/${vars[0]}/native/ContrastProfiler.so`
          },
          {
            var: 'CORECLR_PROFILER_PATH_32',
            val: `<DirectoryRoot>/contrast/runtimes/${vars[1]}/native/ContrastProfiler.so`
          },
          {
            var: 'CORECLR_PROFILER',
            val: '{8B2CE134-0948-48CA-A4B2-80DDAD9F5791}'
          },
          { var: 'CORECLR_ENABLE_PROFILING', val: '1' },
          {
            var: 'CONTRAST_CONFIG_PATH',
            val: '<path_to_contrast_security.yaml>'
          }
        ]

  // windows tables do not diplay this row
  if (type !== 'windows') {
    data.splice(1, 1)
  }

  return data
}

const stepAgentConfig = {
  titleMsg: 'title_configureTheAgent',
  Content: () => <AgentConfig />
}

const stepAgentConfigPipeline = {
  titleMsg: 'title_configureTheAgent',
  Content: () => <AgentConfig isForPipeline={true} />
}

const stepBuildApplication = (code, detailMsg = '') => ({
  titleMsg: 'title_buildApplication',
  Content: () =>
    useIntl().formatMessage(
      { id: detailMsg || 'detail_buildApplication' },
      { code }
    )
})

const stepConfigureAppSettingsAzure = {
  titleMsg: 'title_configureAppSettings',
  Content: () => {
    const ctx = React.useContext(WizardStepContext)
    return useIntl().formatMessage(
      { id: 'detail_dotnetAzureConfigAppSettings' },
      {
        ...formatStyles.table({
          columns: getColumnsEnv(useIntl()),
          data: [
            { var: 'CONTRAST__API__URL', val: ctx?.apiCredentials?.agentUrl },
            {
              var: 'CONTRAST__API__API_KEY',
              val: ctx?.apiCredentials?.api_key
            },
            {
              var: 'CONTRAST__API__SERVICE_KEY',
              val: ctx?.apiCredentials?.service_key
            },
            {
              var: 'CONTRAST__API__USER_NAME',
              val: ctx?.apiCredentials?.user_uid
            },
            { var: 'CONTRAST__APPLICATION__NAME', val: '' },
            { var: 'CONTRAST__APPLICATION__GROUP', val: '' },
            { var: 'CONTRAST__APPLICATION__TAGS', val: '' },
            { var: 'CONTRAST__APPLICATION__METADATA', val: '' }
          ]
        })
      }
    )
  }
}

const stepConfigureAppSettingsAzureNormalize = {
  titleMsg: 'title_configureAppSettings',
  Content: () => {
    const ctx = React.useContext(WizardStepContext)
    return useIntl().formatMessage(
      { id: 'detail_dotnetAzureConfigAppSettings' },
      {
        ...formatStyles.table({
          columns: getColumnsEnv(useIntl()),
          data: [
            { var: 'CONTRAST__API__TOKEN', val: ctx?.apiCredentials?.token },
            { var: 'CONTRAST__APPLICATION__NAME', val: '' },
            { var: 'CONTRAST__APPLICATION__GROUP', val: '' },
            { var: 'CONTRAST__APPLICATION__TAGS', val: '' },
            { var: 'CONTRAST__APPLICATION__METADATA', val: '' }
          ]
        })
      }
    )
  }
}

const stepSelectContainerMethod = {
  defaultIsOpen: true,
  titleMsg: 'title_selectDeployMethod',
  Content: {
    options: [
      {
        value: 'docker',
        Content: 'Dockerfile',
        icon: <ImageIcon src={logo_docker} alt="Dockerfile" />
      },
      {
        value: 'kubernetes',
        Content: 'Kubernetes',
        icon: <ImageIcon src={logo_kubernetes} alt="Kubernetes" />
      }
    ]
  }
  // requires: branchKey: '', branchOptions { docker: [...], kubernetes: [...] }
}

const stepSelectDockerMethod = {
  ...stepSelectContainerMethod,
  Content: {
    ...stepSelectContainerMethod.Content,
    options: stepSelectContainerMethod.Content.options.filter(
      (it) => it.value === 'docker'
    )
  }
}

const stepSelectKubernetesOperator = {
  defaultIsOpen: true,
  titleMsg: 'title_selectMethodToConfigureAgentOperator',
  Content: {
    postDetailMsg: {
      message: 'detail_selectMethodToConfigureAgentOperator',
      values: formatStyles.link({
        isExternal: true,
        href: 'https://helm.sh/docs/intro/install/'
      })
    },
    options: [
      {
        value: 'helm',
        Content: <RecommendedOption Content="Helm charts" />
      },
      { value: 'manifest', Content: 'Manifest' }
    ]
  }
  // requires: branchKey, branchOptions
}

const stepSelectOperatingSystem = {
  defaultIsOpen: true,
  titleMsg: 'title_selectOS',
  Content: {
    detailMsg: 'detail_selectOS',
    options: [
      {
        value: OPERATING_SYSTEM.LINUX,
        Content: 'Linux',
        icon: <ImageIcon src={logo_linux} alt="Linux" />
      },
      {
        value: OPERATING_SYSTEM.WINDOWS,
        Content: 'Windows',
        icon: <ImageIcon src={logo_windows} alt="Windows" />
      }
    ]
  },
  branchKey: OPERATING_SYSTEM.KEY
  // requires: branchOptions { linux: [...], windows: [...] }
}

const stepSelectOperatingSystemNormalize = {
  defaultIsOpen: true,
  titleMsg: 'title_selectOS',
  Content: {
    options: [
      {
        value: OPERATING_SYSTEM.LINUX,
        Content: 'Linux',
        icon: <ImageIcon src={logo_linux} alt="Linux" />
      },
      {
        value: OPERATING_SYSTEM.WINDOWS,
        Content: 'Windows',
        icon: <ImageIcon src={logo_windows} alt="Windows" />
      },
      {
        value: OPERATING_SYSTEM.MACOS,
        Content: 'macOS',
        icon: <ImageIcon src={logo_os} alt="OS" />
      }
    ]
  },
  branchKey: OPERATING_SYSTEM.KEY
  // requires: branchOptions { linux: [...], windows: [...] }
}

const stepSelectWindowsNormalize = {
  ...stepSelectOperatingSystem,
  Content: {
    options: stepSelectOperatingSystem.Content.options.filter(
      (it) => it.value === OPERATING_SYSTEM.WINDOWS
    )
  }
}
const stepSelectLinuxNormalize = {
  ...stepSelectOperatingSystem,
  Content: {
    options: stepSelectOperatingSystem.Content.options.filter(
      (it) => it.value === OPERATING_SYSTEM.LINUX
    )
  }
}
const stepSelectOperatingSystemLegacyNoDetails = {
  ...stepSelectOperatingSystem,
  Content: {
    options: stepSelectOperatingSystem.Content.options
  }
}
const stepSelectLinuxMacOSNormalize = {
  ...stepSelectOperatingSystemNormalize,
  Content: {
    options: stepSelectOperatingSystemNormalize.Content.options.filter(
      (it) =>
        it.value === OPERATING_SYSTEM.LINUX ||
        it.value === OPERATING_SYSTEM.MACOS
    )
  }
}

const ReadMoreDocsInfo = () => {
  const intl = useIntl()
  const language = getCurrentLanguage().toLowerCase()
  const path =
    language === 'dotnet_core'
      ? '-net-core.html'
      : language === 'dotnet'
      ? '-net-framework.html'
      : language === 'node'
      ? 'node-js-agent.html'
      : `${language}.html`

  return (
    <Stack
      flexDirection="column"
      className={css`
        margin-top: 16px;
        border-radius: 4px;
        padding: 24px;
        background-color: ${theme.color.teal10};
      `}
    >
      <Text font="titleH2">
        {intl.formatMessage({ id: 'title_readMoreInDocumentation' })}
      </Text>
      <Text font="body">
        {intl.formatMessage(
          { id: 'detail_readMoreInDocumentation' },
          // links to main docs page
          { ...formatStyles.doclink({ path }) }
        )}
      </Text>
    </Stack>
  )
}

const stepInteractContrastUI = {
  titleMsg: 'title_interactAppContrastUI',
  Content: () => {
    const intl = useIntl()
    const ctx: WizardStepContextValueType = React.useContext(WizardStepContext)

    return (
      <Stack flexDirection="column">
        <Text font="body">
          {intl.formatMessage(
            { id: 'detail_interactAppContrastUI' },
            {
              ...formatStyles.button({
                className: css`
                  max-width: 270px;
                `,
                onClick: () => {
                  const { appUuid } = ctx

                  location.hash = appUuid
                    ? getApplicationDrilldownRoute(getOrgUuid(), appUuid)
                    : getApplicationsRoute(getOrgUuid())
                }
              })
            }
          )}
        </Text>
        <ReadMoreDocsInfo />
      </Stack>
    )
  }
}

const ContentDownloadCode = ({ message, codeTemplate, values = {} }) => {
  const intl = useIntl()
  const ctx = React.useContext(WizardStepContext)
  const code = populateCode(codeTemplate, ctx.apiCredentials)

  return intl.formatMessage(
    { id: message },
    {
      code,
      ...values,
      ...formatStyles.button({
        ...useDownloadCodeblock({ code })
      })
    }
  )
}

const CodeBlockFullHeight = ({ codeTemplate }) => {
  const intl = useIntl()
  const ctx = React.useContext(WizardStepContext)
  const code = populateCode(codeTemplate, ctx.apiCredentials)
  const { onClick: onClickDownloadYaml } = useDownloadCodeblock(
    { code },
    'file.yaml'
  )

  return (
    <>
      <CodeBlock style={{ width: '100%', maxHeight: 'none' }} message={code} />
      <br></br>
      <Button
        className={css`
          width: 164px;
          float: right;
        `}
        onClick={onClickDownloadYaml}
        Icon={IconDownload}
      >
        {intl.formatMessage({ id: 'title_downloadYAML' })}
      </Button>
    </>
  )
}

const stepDownloadContrastAgent = (detailMsg, agent) => ({
  defaultIsOpen: true,
  titleMsg: 'title_downloadInstaller',
  Content: () => {
    const intl = useIntl()
    const [isDownloading, setIsDownloading] = React.useState(false)
    const onDownload = React.useCallback(async () => {
      setIsDownloading(true)

      await downloadAgent({
        organizationID: getOrgUuid(),
        agent,
        formatMessage: intl.formatMessage
      })

      setIsDownloading(false)
    }, [intl])

    return (
      <Button
        isDisabled={isDownloading}
        style={{
          cursor: isDownloading ? 'wait' : 'pointer',
          maxWidth: '100%'
        }}
        variant="primary"
        onClick={isDownloading ? undefined : onDownload}
      >
        {intl.formatMessage({ id: detailMsg })}
      </Button>
    )
  }
})

export {
  // steps & step templates
  stepAgentConfig,
  stepAgentConfigPipeline,
  stepBuildApplication,
  stepConfigureAppSettingsAzure,
  stepConfigureAppSettingsAzureNormalize,
  stepInteractContrastUI,
  stepSelectContainerMethod,
  stepSelectDockerMethod,
  stepSelectKubernetesOperator,
  stepSelectLinuxNormalize,
  stepSelectLinuxMacOSNormalize,
  stepSelectOperatingSystem,
  stepSelectWindowsNormalize,
  stepSelectOperatingSystemLegacyNoDetails,
  stepSelectOperatingSystemNormalize,
  // utils
  populateCode,
  getColumnsEnv,
  getEnvVarsData,
  useYamlPath,
  // Components
  ContentDownloadCode,
  CodeBlockFullHeight,
  stepDownloadContrastAgent,
  ReadMoreDocsInfo
}
